import { groupBy, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';
import { Matrix4 } from 'three';

import { Truss } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import { RootState } from '../store';
import { getProductionComponentInstancesByBatchGuid } from './batches';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { getLoadedTrussesByAnalysisSetGuid } from './trusses';

export const getTrussesForStackedTrussPreview = createSelector(
  (state: RootState) => state.batchesAndBundlesTab.batchBeingEditedGuid,
  (state: RootState) => state.batchesAndBundlesTab.bundleBeingEditedGuid,
  (state: RootState) => state.batchesAndBundlesTab.checkedAnalysisSetGuids,
  getProductionComponentInstancesByBatchGuid,
  getProductionComponentInstancesByBundleGuid,
  getLoadedTrussesByAnalysisSetGuid,

  (
    batchBeingEditedGuid,
    bundleBeingEditedGuid,
    analysisSetGuidsForCheckedProductionGroupGridRows,
    productionComponentInstancesByBatchGuid,
    productionComponentInstancesByBundleGuid,
    loadedTrussesByAnalysisSetGuid,
  ) => {
    const trussesForPreview: { [key: string]: { truss: Truss; matrix?: readonly number[] } } = {};
    // If editing a batch or bundle, show those component designs
    let analysisSetGuidsInGroupBeingEdited: string[] = [];
    if (batchBeingEditedGuid !== null) {
      analysisSetGuidsInGroupBeingEdited = productionComponentInstancesByBatchGuid[batchBeingEditedGuid].map(
        (productionComponentInstance) => productionComponentInstance.analysisSetGuid,
      );
    } else if (bundleBeingEditedGuid !== null) {
      // Account for trusses in bundles in different x positions
      const instancesInBundleGroupedByAnalysisSetGuid = groupBy(
        productionComponentInstancesByBundleGuid[bundleBeingEditedGuid],
        (productionComponentInstance) => productionComponentInstance.analysisSetGuid,
      );
      for (const [analysisSetGuid, instances] of Object.entries(instancesInBundleGroupedByAnalysisSetGuid)) {
        const truss = loadedTrussesByAnalysisSetGuid[analysisSetGuid];
        if (!truss) continue;
        const xPositions = uniq(instances.map((instance) => instance.bundlePositionX)).filter(notNullish);
        for (const xPosition of xPositions) {
          const matrix = new Matrix4().makeTranslation(xPosition, 0, 0).elements;
          trussesForPreview[`${analysisSetGuid}-${xPosition}`] = { truss, matrix };
        }
      }
    }

    const analysisSetGuidsToShowAtOrigin = analysisSetGuidsInGroupBeingEdited.concat(
      analysisSetGuidsForCheckedProductionGroupGridRows,
    );

    for (const analysisSetGuidToShowAtOrigin of uniq(analysisSetGuidsToShowAtOrigin)) {
      const truss = loadedTrussesByAnalysisSetGuid[analysisSetGuidToShowAtOrigin];
      if (!truss) continue;
      trussesForPreview[analysisSetGuidToShowAtOrigin] = { truss };
    }

    return trussesForPreview;
  },
);
